import React, { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode.react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { roadmakerApi } from "../../api/roadmakerApi";

// Page publique /share/:token (ADR 0006) : destinée au destinataire d'un
// lien de partage, sans connexion. Elle montre le nom du parcours, le QR
// à scanner dans RoadPlayer et les liens stores. Le QR encode le lien
// canonique de la page (sans query ni fragment) ; le jeton n'est jamais
// affiché en clair et rien n'est stocké localement.
const LOGO_CONNEXION_IMG = "/rm_imgs/logo_connexion.png";
const ANDROID_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.mh.roadplayer";
const IOS_STORE_URL = "https://apps.apple.com/fr/app/road-player/id6739284205";

const shareLinkFor = (token) =>
  `${window.location.origin}/share/${encodeURIComponent(token)}`;

const SharePage = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading", project: null });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading", project: null });
    roadmakerApi.shares
      .readProject(token)
      .then((project) => {
        if (!cancelled) setState({ status: "success", project });
      })
      .catch((error) => {
        if (cancelled) return;
        // 404 = jeton inconnu, révoqué ou expiré (le backend ne distingue
        // pas) ; tout le reste (réseau, 5xx) reçoit le message générique.
        setState({
          status: error?.status === 404 ? "invalid" : "unavailable",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const link = useMemo(() => (token ? shareLinkFor(token) : ""), [token]);

  return (
    <main className="container d-flex flex-column align-items-center py-4 px-3">
      <img
        src={LOGO_CONNEXION_IMG}
        alt="RoadPlayer"
        className="mb-3"
        style={{ maxHeight: 120, maxWidth: 200 }}
      />
      {state.status === "loading" && (
        <div className="text-center" data-testid="share-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">
              {t("sharePage.loading")}
            </span>
          </div>
          <p className="mt-2 mb-0">{t("sharePage.loading")}</p>
        </div>
      )}
      {state.status === "invalid" && (
        <div className="alert alert-warning text-center" role="alert">
          {t("sharePage.invalidLink")}
        </div>
      )}
      {state.status === "unavailable" && (
        <div className="alert alert-danger text-center" role="alert">
          {t("sharePage.unavailable")}
        </div>
      )}
      {state.status === "success" && (
        <section className="card shadow-sm w-100" style={{ maxWidth: 480 }}>
          <div className="card-body text-center p-3 p-sm-4">
            <h1 className="card-title h4 mb-2" data-testid="share-project-name">
              {state.project?.name || t("sharePage.unnamedProject")}
            </h1>
            {state.project?.description ? (
              <p className="text-muted mb-3">{state.project.description}</p>
            ) : null}
            <h2 className="h6 mt-3">{t("sharePage.howToTitle")}</h2>
            <ol className="text-start mx-auto px-3" style={{ maxWidth: 420 }}>
              <li>{t("sharePage.step1")}</li>
              <li>{t("sharePage.step2")}</li>
            </ol>
            <div
              className="d-flex justify-content-center my-3"
              aria-label={t("sharePage.qrLabel")}
            >
              <QRCode value={link} size={192} includeMargin />
            </div>
            <p className="fw-bold mb-2">{t("sharePage.getApp")}</p>
            <div className="d-flex flex-wrap gap-2 justify-content-center">
              <a
                data-testid="store-android"
                className="btn btn-primary"
                href={ANDROID_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("sharePage.android")}
              </a>
              <a
                data-testid="store-ios"
                className="btn btn-dark"
                href={IOS_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("sharePage.ios")}
              </a>
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default SharePage;
